import type { LegalDocument } from '../types';
import { PRIVACY_VERSIONE_CORRENTE } from '../currentVersions';

export const PRIVACY_IT: LegalDocument = {
  kind: 'privacy_policy',
  titolo: 'Informativa sulla privacy',
  sottotitolo: 'Marketplace FV — Trattamento dei dati personali ai sensi del GDPR (UE) 2016/679',
  versione: PRIVACY_VERSIONE_CORRENTE,
  data_ultima_modifica: '2026-05-16',
  data_efficacia: '2026-05-16',
  in_preparazione: true,
  introduzione: [
    {
      type: 'text',
      text: 'La presente informativa descrive le modalita di trattamento dei dati personali raccolti tramite la piattaforma Marketplace FV ai sensi del Regolamento UE 2016/679 (GDPR) e della normativa italiana in materia di protezione dei dati personali (D.Lgs. 196/2003 e s.m.i.).',
    },
    {
      type: 'note',
      text: '**Documento in fase di preparazione.** I testi definitivi sono in fase di redazione e revisione legale. La struttura del documento e le sezioni richieste dal GDPR sono quelle definitive: cambieranno solo i contenuti specifici.',
    },
  ],
  sezioni: [
    {
      id: '1-titolare',
      numero: '1',
      titolo: 'Titolare del trattamento',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — identificazione completa del Titolare del trattamento: ragione sociale, sede legale, P.IVA, riferimenti contatto (email, PEC, telefono). Eventuale designazione del Responsabile della Protezione dei Dati (DPO) e suoi contatti.]',
        },
      ],
    },
    {
      id: '2-tipi-dati',
      numero: '2',
      titolo: 'Tipi di dati personali raccolti',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — elenco categorizzato dei dati raccolti:',
        },
        {
          type: 'bullets',
          items: [
            'Dati identificativi dell\'azienda e del rappresentante legale (ragione sociale, P.IVA, REA, sede)',
            'Dati di contatto (email aziendale, telefono, indirizzo)',
            'Dati di autenticazione (email, password cifrata gestita da Firebase Auth, oppure account Google collegato)',
            'Dati di navigazione (indirizzo IP, user agent, log accessi)',
            'Dati operativi del marketplace (richieste pubblicate, offerte ricevute, match accettati)',
            'Dati amministrativi (Partita IVA, dati per fatturazione, modalita di pagamento)',
          ],
        },
      ],
    },
    {
      id: '3-finalita',
      numero: '3',
      titolo: 'Finalità del trattamento e basi giuridiche',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — questa sezione conterra una tabella che, per ogni finalita, indica:',
        },
        {
          type: 'bullets',
          items: [
            'Finalita: erogazione del servizio (esecuzione del contratto, art. 6.1.b GDPR)',
            'Finalita: KYC e verifica P.IVA (obblighi legali e antifrode, art. 6.1.c e 6.1.f GDPR)',
            'Finalita: comunicazioni di servizio (esecuzione contratto, art. 6.1.b)',
            'Finalita: comunicazioni di marketing (consenso esplicito, art. 6.1.a)',
            'Finalita: sicurezza, prevenzione abuso e tutela legale (legittimo interesse, art. 6.1.f)',
            'Finalita: adempimenti fiscali e contabili (obblighi legali, art. 6.1.c)',
          ],
        },
      ],
    },
    {
      id: '4-modalita',
      numero: '4',
      titolo: 'Modalità di trattamento',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — i dati sono trattati con strumenti elettronici idonei a garantire la sicurezza e la riservatezza, mediante: cifratura in transito (TLS) e a riposo (Firestore/Cloud Storage), controlli di accesso role-based, audit log immutabili delle operazioni rilevanti, backup giornalieri.]',
        },
      ],
    },
    {
      id: '5-conservazione',
      numero: '5',
      titolo: 'Periodo di conservazione',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — periodi di conservazione per categoria:',
        },
        {
          type: 'bullets',
          items: [
            'Dati account attivo: per tutta la durata dell\'iscrizione',
            'Dati post-cancellazione account: 30 giorni per riattivazione, poi anonimizzazione',
            'Audit log onboarding: 5 anni (sicurezza e antifrode)',
            'Dati fiscali e contabili: 10 anni (obblighi normativi italiani)',
            'Backup tecnici: rotazione 8 settimane',
          ],
        },
      ],
    },
    {
      id: '6-destinatari',
      numero: '6',
      titolo: 'Destinatari dei dati',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — categorie di destinatari, ognuno con ruolo (Responsabile del trattamento ex art. 28 GDPR oppure titolare autonomo) e nazionalita:',
        },
        {
          type: 'bullets',
          items: [
            'Google Cloud / Firebase (hosting, database, autenticazione) — Responsabile, USA con SCC',
            'Google Ireland Limited (Google Sign-In) — Titolare autonomo per la fase di autenticazione',
            'Stripe Payments Europe (gestione pagamenti) — Responsabile, IE',
            'Provider VIES (Commissione UE) per verifica P.IVA — Titolare autonomo, UE',
            'Eventuali consulenti, commercialisti, autorita richiedenti per legge',
          ],
        },
      ],
    },
    {
      id: '7-trasferimenti-extra-ue',
      numero: '7',
      titolo: 'Trasferimento dei dati extra UE',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — alcuni servizi (Google Cloud, Stripe) possono comportare trasferimento di dati negli Stati Uniti. Tali trasferimenti avvengono nel rispetto delle Clausole Contrattuali Standard (SCC) approvate dalla Commissione Europea e/o nel quadro del EU-US Data Privacy Framework. Riferimenti documentali ai DPA dei provider.]',
        },
      ],
    },
    {
      id: '8-diritti-interessato',
      numero: '8',
      titolo: 'Diritti dell\'interessato',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — l\'interessato ha diritto, ai sensi degli articoli 15-22 GDPR, di:',
        },
        {
          type: 'bullets',
          items: [
            'Accedere ai propri dati e ottenerne copia (art. 15)',
            'Rettificare dati inesatti (art. 16)',
            'Cancellare i dati ("diritto all\'oblio", art. 17), nei limiti previsti dagli obblighi di legge',
            'Limitare il trattamento (art. 18)',
            'Ricevere i dati in formato strutturato e portabile (art. 20)',
            'Opporsi al trattamento (art. 21), incluso il marketing diretto',
            'Revocare il consenso al marketing in ogni momento, senza pregiudicare la liceita del trattamento basato sul consenso prima della revoca',
          ],
        },
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — modalita di esercizio dei diritti: invio richiesta a email o PEC del Titolare. Tempi di risposta entro 30 giorni.]',
        },
      ],
    },
    {
      id: '9-reclamo-garante',
      numero: '9',
      titolo: 'Diritto di reclamo al Garante',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — l\'interessato ha diritto di presentare reclamo all\'Autorita Garante per la Protezione dei Dati Personali italiana (www.garanteprivacy.it) o all\'autorita di controllo dello Stato membro UE di residenza, qualora ritenga che il trattamento dei propri dati violi il GDPR.]',
        },
      ],
    },
    {
      id: '10-cookie',
      numero: '10',
      titolo: 'Cookie e tecnologie similari',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — la Piattaforma utilizza cookie tecnici essenziali al funzionamento (sessione di autenticazione Firebase, preferenze di lingua) e, previo consenso, cookie analitici e di terze parti. Per l\'elenco dettagliato e la possibilita di gestire le preferenze si rimanda alla cookie policy dedicata.]',
        },
      ],
    },
    {
      id: '11-modifiche',
      numero: '11',
      titolo: 'Modifiche alla presente informativa',
      paragrafi: [
        {
          type: 'text',
          text: '[CONTENUTO IN PREPARAZIONE — la presente informativa puo essere aggiornata. Le modifiche sostanziali saranno notificate via email e nella web app. La versione corrente e la data di ultima modifica sono indicate in apertura del documento.]',
        },
      ],
    },
  ],
  contatti_titolare: {
    ragione_sociale: '[Ragione sociale del Titolare — DA DEFINIRE]',
    indirizzo: '[Indirizzo sede legale — DA DEFINIRE]',
    email: 'privacy@[dominio-da-definire].it',
    pec: '[PEC — DA DEFINIRE]',
    pec_dpo: '[PEC DPO — opzionale, DA DEFINIRE]',
  },
};
